/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';
import { FormattedMessage, injectIntl } from 'react-intl';

import ShareCardSummaryCategoryTitle from './ShareCardSummaryCategoryTitle';
import ShareCardSummaryGroup from './ShareCardSummaryGroup';

import { ShareCardSummaryHealthEntries, ShareCardSummaryTrackables } from './ShareCardSummaryOccurrences';
import ShareCardIncludedTypeNames from '../../../../constants/ShareCardIncludedTypeNames';
import { useTrackableListForActiveProfile } from '../../../hooks/useTrackable';
import healthEntryUtility from '../../../../models/HealthEntryUtility';

interface Props {
  shareCard: any;
  categoryName: string;
  newerCollection: any[];
  olderCollection: any[];
  hasTitle?: boolean;
  languageCode: string;
  includedValues: ShareCardIncludedTypeNames;
  intl?: any;
}
export const ShareCardSummaryHealthEntryGroup: React.FC<Props> = (props) => {
  const newerEntries = props.newerCollection.filter((he) => {
    const inShareCard = props.shareCard.healthEntry.isIncludedWithinType(he.id, props.includedValues);
    const hasHeValue = !he.values || he.values.hasValues();
    return inShareCard && hasHeValue;
  });
  const olderEntries = props.olderCollection.filter((he) => {
    const inShareCard = props.shareCard.healthEntry.isIncludedWithinType(he.id, props.includedValues);
    const hasHeValue = !he.values || he.values.hasValues();
    return inShareCard && hasHeValue;
  });

  if ((newerEntries.length + olderEntries.length) === 0) { return null; }
  return (
    <ShareCardSummaryGroup>
      <ShareCardSummaryCategoryTitle categoryName={props.categoryName} includedValues={props.includedValues} />

      <div>
        <ShareCardSummaryHealthEntries
          title={props.hasTitle ? <FormattedMessage id="ui-data.healthentryperiod.current.text" /> : null}
          collection={newerEntries}
          languageCode={props.languageCode}
          includedValues={props.includedValues}
          shareCard={props.shareCard}
        />
        <ShareCardSummaryHealthEntries
          title={props.hasTitle ? <FormattedMessage id="ui-data.healthentryperiod.past.text" /> : null}
          collection={olderEntries}
          languageCode={props.languageCode}
          includedValues={props.includedValues}
          shareCard={props.shareCard}
        />
      </div>
    </ShareCardSummaryGroup>
  );
};

export const ShareCardSummaryAllergyGroup: React.FC<Props> = injectIntl((props) => {
  const newerCollection = healthEntryUtility.sortAllergies(props.newerCollection, props.languageCode, props.intl);
  const olderCollection = healthEntryUtility.sortAllergies(props.olderCollection, props.languageCode, props.intl);

  return (
    <ShareCardSummaryHealthEntryGroup
      {...props}
      newerCollection={newerCollection}
      olderCollection={olderCollection}
    />
  );
});

interface ShareCardSummaryTrackableGroupProps {
  includedValues: ShareCardIncludedTypeNames;
  languageCode: string;
  shareCard: any;
}
export const ShareCardSummaryTrackableGroup: React.FC<ShareCardSummaryTrackableGroupProps> = (props) => {
  const { includedValues, languageCode, shareCard } = props;

  const [{ trackableTermLookupByLanguage }, { getShareCardSorted }] = useTrackableListForActiveProfile();
  const sortedTrackables = getShareCardSorted(shareCard, includedValues, languageCode);

  if (sortedTrackables.length === 0) { return null; }

  return (
    <ShareCardSummaryGroup>
      <ShareCardSummaryCategoryTitle categoryName="trackables" includedValues={includedValues} />

      <div>
        <ShareCardSummaryTrackables
          title={null}
          shareCard={shareCard}
          trackables={sortedTrackables}
          terms={trackableTermLookupByLanguage[languageCode]}
          includedTypeValue={includedValues}
          languageCode={languageCode}
        />
      </div>
    </ShareCardSummaryGroup>
  );
};
